import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import {
  MapPin, Phone, Mail, Clock, MessageCircle, ArrowRight, Loader2, Calendar as CalIcon,
} from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "../components/ui/calendar";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { BRAND, SERVICES } from "../data/mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIME_SLOTS = [
  "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
  "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM",
  "5:00 PM", "6:00 PM", "7:00 PM",
];

const inputCls =
  "w-full bg-[#1F0A66]/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:border-[#FC5106] focus:ring-1 focus:ring-[#FC5106] outline-none transition-all";

export default function Contact() {
  const [date, setDate] = useState();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    pet_name: "",
    pet_type: "Dog",
    pet_breed: "",
    service: "",
    time_slot: "",
    location: "Studio",
    message: "",
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service || !date || !form.time_slot) {
      toast.error("Please fill name, phone, service, date and time slot.");
      return;
    }
    setSubmitting(true);
    try {
      const formattedDate = format(date, "PPP");
      const payload = {
        ...form,
        date: format(date, "yyyy-MM-dd"),
      };
      // 1) Save to backend
      await axios.post(`${API}/bookings`, payload);

      // GA4 conversion: lead generated from contact form
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", {
          event_category: "conversion",
          event_label: "contact_form_booking",
          method: "form",
          service: form.service,
          location: form.location,
        });
      }

      // 2) Build WhatsApp lead message and forward to studio
      const lines = [
        "*New Booking Enquiry — BarknFurs*",
        "",
        `*Name:* ${form.name}`,
        `*Phone:* ${form.phone}`,
        form.email ? `*Email:* ${form.email}` : null,
        "",
        `*Pet:* ${form.pet_name || "—"} (${form.pet_type}${
          form.pet_breed ? ` · ${form.pet_breed}` : ""
        })`,
        `*Service:* ${form.service}`,
        `*Location:* ${
          form.location === "Home" ? "Home Grooming (Doorstep)" : "At Studio"
        }`,
        `*Preferred Date:* ${formattedDate}`,
        `*Time Slot:* ${form.time_slot}`,
        form.message ? `\n*Notes:* ${form.message}` : null,
      ].filter(Boolean);

      const waUrl = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(
        lines.join("\n")
      )}`;

      toast.success("Booking received! Opening WhatsApp to confirm…");

      // Reset form
      setForm({
        name: "", email: "", phone: "", pet_name: "", pet_type: "Dog",
        pet_breed: "", service: "", time_slot: "", location: "Studio", message: "",
      });
      setDate(undefined);

      // Slight delay so the toast renders, then redirect to WhatsApp
      setTimeout(() => {
        window.open(waUrl, "_blank", "noopener,noreferrer");
      }, 600);
    } catch (err) {
      toast.error("Could not submit booking. Please try WhatsApp or call us directly.");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const wa = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(
    "Hi BarknFurs! I'd like to book a grooming session."
  )}`;

  return (
    <div data-testid="contact-page" className="bg-[#3915AA]">
      {/* Header */}
      <section className="section-padding container-custom">
        <div className="max-w-3xl">
          <p className="text-[#C9BCFF] uppercase tracking-[0.25em] text-xs mb-4">Contact & booking</p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.02]">
            Let's get your pet <span className="text-gradient-primary">looking fabulous</span>.
          </h1>
          <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-2xl">
            Drop us your details below and we'll WhatsApp you to confirm your slot. Prefer to chat?
            Call or WhatsApp us — we usually reply within minutes.
          </p>
        </div>
      </section>

      {/* Contact strip */}
      <section className="container-custom pb-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              icon: Phone,
              title: "Call us",
              value: BRAND.phone,
              href: `tel:${BRAND.phoneRaw}`,
              accent: "text-[#FC5106]",
              testid: "info-call",
            },
            {
              icon: MessageCircle,
              title: "WhatsApp",
              value: BRAND.phone,
              href: wa,
              accent: "text-[#25D366]",
              testid: "info-whatsapp",
            },
            {
              icon: Mail,
              title: "Email",
              value: BRAND.email,
              href: `mailto:${BRAND.email}`,
              accent: "text-[#C9BCFF]",
              testid: "info-email",
            },
            {
              icon: Clock,
              title: "Hours",
              value: BRAND.hours,
              accent: "text-[#FC5106]",
              testid: "info-hours",
            },
          ].map((c) => {
            const Inner = (
              <>
                <c.icon className={`w-5 h-5 ${c.accent} mb-3`} />
                <p className="text-white/50 uppercase text-xs tracking-[0.2em] mb-1">{c.title}</p>
                <p className="text-white font-semibold text-sm">{c.value}</p>
              </>
            );
            return c.href ? (
              <a
                key={c.title}
                href={c.href}
                data-testid={c.testid}
                className="rounded-2xl border border-white/10 bg-[#2E0F94] p-6 hover:border-white/30 transition-colors block"
                target={c.href.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
              >
                {Inner}
              </a>
            ) : (
              <div key={c.title} data-testid={c.testid} className="rounded-2xl border border-white/10 bg-[#2E0F94] p-6">
                {Inner}
              </div>
            );
          })}
        </div>
      </section>

      {/* Booking form + Map */}
      <section className="container-custom pb-24">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="booking-form"
              className="rounded-3xl border border-white/10 bg-[#2E0F94] p-7 md:p-9"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">
                Book your appointment
              </h2>
              <p className="text-white/60 text-sm mb-7">All fields marked * are required.</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-white/60 uppercase tracking-wider mb-1.5 block">
                    Your name *
                  </label>
                  <input
                    data-testid="input-name"
                    className={inputCls}
                    placeholder="e.g. Ananya Mishra"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs text-white/60 uppercase tracking-wider mb-1.5 block">
                    Phone / WhatsApp *
                  </label>
                  <input
                    data-testid="input-phone"
                    className={inputCls}
                    placeholder="98XXXXXXXX"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs text-white/60 uppercase tracking-wider mb-1.5 block">
                    Email
                  </label>
                  <input
                    data-testid="input-email"
                    type="email"
                    className={inputCls}
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs text-white/60 uppercase tracking-wider mb-1.5 block">
                    Pet's name
                  </label>
                  <input
                    data-testid="input-pet-name"
                    className={inputCls}
                    placeholder="e.g. Coco"
                    value={form.pet_name}
                    onChange={(e) => update("pet_name", e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs text-white/60 uppercase tracking-wider mb-1.5 block">
                    Pet type
                  </label>
                  <Select value={form.pet_type} onValueChange={(v) => update("pet_type", v)}>
                    <SelectTrigger data-testid="select-pet-type" className={inputCls + " text-left"}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#2E0F94] border-white/10 text-white">
                      <SelectItem value="Dog">Dog</SelectItem>
                      <SelectItem value="Cat">Cat</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-white/60 uppercase tracking-wider mb-1.5 block">
                    Breed
                  </label>
                  <input
                    data-testid="input-pet-breed"
                    className={inputCls}
                    placeholder="e.g. Golden Retriever"
                    value={form.pet_breed}
                    onChange={(e) => update("pet_breed", e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs text-white/60 uppercase tracking-wider mb-1.5 block">
                    Service *
                  </label>
                  <Select value={form.service} onValueChange={(v) => update("service", v)}>
                    <SelectTrigger data-testid="select-service" className={inputCls + " text-left"}>
                      <SelectValue placeholder="Pick a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#2E0F94] border-white/10 text-white max-h-64">
                      {SERVICES.map((s) => (
                        <SelectItem key={s.id} value={s.title}>
                          {s.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-white/60 uppercase tracking-wider mb-1.5 block">
                    Location
                  </label>
                  <Select value={form.location} onValueChange={(v) => update("location", v)}>
                    <SelectTrigger data-testid="select-location" className={inputCls + " text-left"}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#2E0F94] border-white/10 text-white">
                      <SelectItem value="Studio">At Studio (Sailashree Vihar)</SelectItem>
                      <SelectItem value="Home">Home Grooming (doorstep)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-white/60 uppercase tracking-wider mb-1.5 block">
                    Preferred date *
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="button-date-picker"
                        className={`${inputCls} text-left flex items-center justify-between`}
                      >
                        <span className={date ? "text-white" : "text-white/40"}>
                          {date ? format(date, "PPP") : "Pick a date"}
                        </span>
                        <CalIcon className="w-4 h-4 text-[#C9BCFF]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="w-auto p-0 bg-[#2E0F94] border-white/10"
                    >
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                        className="text-white"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <label className="text-xs text-white/60 uppercase tracking-wider mb-1.5 block">
                    Time slot *
                  </label>
                  <Select value={form.time_slot} onValueChange={(v) => update("time_slot", v)}>
                    <SelectTrigger data-testid="select-time-slot" className={inputCls + " text-left"}>
                      <SelectValue placeholder="Pick a time" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#2E0F94] border-white/10 text-white max-h-64">
                      {TIME_SLOTS.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs text-white/60 uppercase tracking-wider mb-1.5 block">
                    Anything we should know?
                  </label>
                  <textarea
                    data-testid="input-message"
                    rows={4}
                    className={inputCls + " resize-none"}
                    placeholder="Allergies, anxious behaviour, special requests…"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="button-submit-booking"
                className="btn-primary mt-7 w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Request booking <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
              <p className="text-center text-white/40 text-xs mt-3">
                On submit we'll open WhatsApp with your details — just tap send.
              </p>
            </form>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-5 space-y-6">
            <div className="rounded-3xl overflow-hidden border border-white/10 bg-[#2E0F94]">
              <div className="aspect-[4/3]">
                <iframe
                  data-testid="contact-map"
                  title="BarknFurs studio location"
                  src="https://www.google.com/maps?q=Sailashree+Vihar+Bhubaneswar&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: "invert(0.92) hue-rotate(180deg) saturate(1.2)" }}
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <div className="flex items-start gap-3 mb-4">
                  <MapPin className="w-5 h-5 text-[#FC5106] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-white font-semibold text-sm">Studio address</p>
                    <p className="text-white/60 text-sm">{BRAND.address}</p>
                  </div>
                </div>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Sailashree+Vihar+Bhubaneswar"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="map-directions-link"
                  className="btn-secondary !py-2.5 !px-5 !text-sm w-full justify-center"
                >
                  Get directions <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>

            <a
              href={wa}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp-card"
              className="block rounded-3xl p-7 border border-[#25D366]/30 bg-gradient-to-br from-[#25D366]/15 to-transparent hover:border-[#25D366]/60 transition-colors"
            >
              <MessageCircle className="w-7 h-7 text-[#25D366] mb-3" strokeWidth={1.5} />
              <h3 className="font-heading text-2xl font-bold text-white mb-1">Prefer WhatsApp?</h3>
              <p className="text-white/70 text-sm mb-4">
                Message us with your pet's photo and we'll instantly help you pick the right service.
              </p>
              <span className="inline-flex items-center gap-2 text-[#25D366] font-semibold">
                Chat on WhatsApp <ArrowRight className="w-4 h-4" />
              </span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
